import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Lock, ArrowUpRight, ArrowLeft } from "lucide-react";
import useSeo from "../hooks/useSeo";

export default function AdminLogin() {
    useSeo({
        title: "Admin · GreenDuck",
        description: "Área restrita da GreenDuck.",
        canonical: "https://www.greenduck.com.br/admin/login",
        noindex: true,
    });
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.access_token) {
                localStorage.setItem("gd_token", data.access_token);
            }
            navigate("/admin");
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Não foi possível entrar.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen w-full bg-[#050505] flex items-center justify-center px-6 relative overflow-hidden">
            <div className="absolute inset-0 gd-gridlines opacity-30 pointer-events-none" />
            <div className="absolute inset-0 hero-aura pointer-events-none" />

            <Link
                to="/"
                className="absolute top-6 left-6 md:top-8 md:left-10 flex items-center gap-2 text-white/60 hover:text-[#00FF66] text-sm font-mono-tech tracking-widest uppercase transition-colors"
                data-testid="back-to-site"
            >
                <ArrowLeft className="w-4 h-4" /> Voltar ao site
            </Link>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md relative z-10"
            >
                <div className="flex items-center gap-3 mb-8">
                    <span className="duck-dot" />
                    <span className="font-display uppercase text-2xl tracking-tight text-white">
                        GreenDuck
                    </span>
                    <span className="font-mono-tech text-xs text-[#00FF66]">
                        /admin
                    </span>
                </div>
                <h1 className="font-display uppercase text-4xl md:text-5xl leading-none tracking-tighter text-white mb-3">
                    Acesso restrito.
                </h1>
                <p className="text-white/50 mb-10">
                    Entre para gerenciar os leads recebidos pelo site.
                </p>
                <form
                    onSubmit={onSubmit}
                    className="border border-white/10 bg-[#0a0a0a] p-8 clip-frame-sm space-y-5"
                    data-testid="admin-login-form"
                >
                    <div>
                        <label className="block text-xs font-mono-tech uppercase tracking-widest text-white/50 mb-2">
                            E-mail
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full bg-transparent border border-white/15 focus:border-[#00FF66] outline-none px-4 py-3 text-white transition-colors"
                            data-testid="admin-email"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-mono-tech uppercase tracking-widest text-white/50 mb-2">
                            Senha
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full bg-transparent border border-white/15 focus:border-[#00FF66] outline-none px-4 py-3 text-white transition-colors"
                            data-testid="admin-password"
                        />
                    </div>
                    {error && (
                        <div className="text-red-400 text-sm font-mono-tech" data-testid="admin-login-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-neon w-full justify-center disabled:opacity-60"
                        data-testid="admin-login-submit"
                    >
                        <Lock className="w-4 h-4" />
                        {loading ? "Entrando..." : "Entrar"}
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                </form>
            </motion.div>
        </div>
    );
}
